import { isPlatformServer } from '@angular/common';
import { ElementRef, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable, of } from 'rxjs';
import { randomString } from '../lib/random-string';

@Injectable({
  providedIn: 'root',
})
export class InViewService {
  private platformId = inject(PLATFORM_ID);
  private elementSubjects = new Map<Element, BehaviorSubject<boolean>>();

  private intersectionObserverMap = new Map<string, IntersectionObserver>();

  private ensureInViewKey(rootEl?: HTMLElement): string | undefined {
    if (!rootEl) return;
    let key = rootEl.getAttribute('data-in-view-key');
    if (!key) {
      key = randomString(6);
      rootEl.setAttribute('data-in-view-key', key);
    }
    return key;
  }

  private getIntersectionObserver(rootMargin: string, rootEl?: HTMLElement) {
    const rootKey = this.ensureInViewKey(rootEl);
    const key = rootEl ? `${rootMargin}-${rootKey}` : rootMargin;
    if (!this.intersectionObserverMap.has(key)) {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            const subject = this.elementSubjects.get(entry.target);
            if (subject) {
              subject.next(entry.isIntersecting);
            }
          });
        },
        { rootMargin, root: rootEl }
      );
      this.intersectionObserverMap.set(key, observer);
    }
    return this.intersectionObserverMap.get(key);
  }

  private getElement(element: ElementRef) {
    if (element.nativeElement instanceof HTMLElement) {
      return element.nativeElement;
    }
    return element.nativeElement.parentElement;
  }

  observe(element: ElementRef<HTMLElement>, rootMargin?: string, rootEl?: HTMLElement): Observable<boolean> {
    if (isPlatformServer(this.platformId)) return of(false);
    const el = this.getElement(element);

    // Return a new Observable for the element
    return new Observable<boolean>(subscriber => {
      // If the element is not already observed, add it
      if (!this.elementSubjects.has(el)) {
        const subject = new BehaviorSubject<boolean>(false);
        this.elementSubjects.set(el, subject);
        const observer = this.getIntersectionObserver(rootMargin, rootEl);
        observer.observe(el);
      }

      // Subscribe to the subject's observable
      const subject = this.elementSubjects.get(el);
      const subscription = subject.asObservable().pipe(distinctUntilChanged()).subscribe(subscriber);

      // Teardown logic to unobserve the element when unsubscribing
      return () => {
        subscription.unsubscribe();
        if (this.elementSubjects.has(el)) {
          const observer = this.getIntersectionObserver(rootMargin, rootEl);
          observer.unobserve(el);
          this.elementSubjects.get(el)?.complete();
          this.elementSubjects.delete(el);
        }
      };
    });
  }
}
